import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "./BlogDetailPage.css";
import { Helmet } from "react-helmet-async";

export default function BlogDetailPage() {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [topAuthors, setTopAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const stripHtml = (html, wordLimit = 30) => {
  if (!html) return "";
  const cleanText = html.replace(/<[^>]+>/g, "").replace(/&[^;]+;/g, "");
  const words = cleanText.trim().split(/\s+/);
  return words.slice(0, wordLimit).join(" ") + (words.length > wordLimit ? "..." : "");
};

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchBlogData = async () => {
      try {
        const response = await fetch(
          "https://aikyam-server-supabaseoff.onrender.com/api/blogs",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch blog data");
        }

        const data = await response.json();

        const formattedBlogs = data.map((blog, index) => ({
          id: blog.id || index,
           slug: blog.slug,
          title: blog.title || "Untitled",
          category: blog.category || "General",
          author: blog.author || "Anonymous",
          date: blog.created_at
            ? new Date(blog.created_at).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "No Date",
          rawDate: blog.created_at || null, 
          time: blog.duration ? `${blog.duration} Min. to Read` : "3 Min. to Read",
          summary: blog.description || "No summary available",
          image: blog.image
            ? blog.image.trim().replace(/\s/g, "%20")
            : "https://via.placeholder.com/300x200?text=No+Image",
        }));

const selectedBlog =
  formattedBlogs.find((blog) => blog.slug === slug) || null;


        const related = selectedBlog
  ? formattedBlogs
      .filter(
        (b) =>
          b.category === selectedBlog.category &&
          b.id !== selectedBlog.id
      )
      .slice(0, 2)
  : [];

        const uniqueAuthors = [
          ...new Set(data.map((blog) => blog.author || "Anonymous")),
        ].slice(0, 3);

        const categoryCounts = data.reduce((acc, blog) => {
          const category = blog.category || "General";
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});
        const formattedCategories = Object.entries(categoryCounts).map(
          ([name, count]) => ({
            name,
            count,
          })
        );

        setBlog(selectedBlog);
        setRelatedBlogs(related);
        setTopAuthors(uniqueAuthors);
        setCategories(formattedCategories);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBlogData();
  }, [slug]);

  if (loading) {
    return (
      <div className="blogsp-main-container">
        <div className="loading-box">
          <div className="loading-spinner"></div>
          <p>Loading....</p>
        </div>
      </div>
    );
  }
  if (error || !blog) {
    return (
      <div className="error-page">
        <img src="/SVG/eror.svg" alt="Error" className="error-image" />
        <Link to="/" className="error-home-button">
          Go to Home Page
        </Link>
      </div>
    );
  }
  const metaDescription = stripHtml(blog.summary, 30);
const canonicalUrl = `https://aikyamsports.com/blog/${blog.slug}`;

  return (
      <>
      <Helmet>
        <title>{blog.title} | Aikyam Sports Blog</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${blog.category}, ${blog.author}, Aikyam Sports, sports science blog`} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:locale" content="en_IN" />
        <meta property="article:author" content={blog.author} />
        <meta property="article:section" content={blog.category} />
        {blog.rawDate && (
          <meta property="article:published_time" content={new Date(blog.rawDate).toISOString()} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={blog.image} />

        {/* BlogPosting structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": metaDescription,
            "image": blog.image,
            "author": {
              "@type": "Person",
              "name": blog.author,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Aikyam Sports",
              "url": "https://aikyamsports.com",
            },
            ...(blog.rawDate && {
              "datePublished": new Date(blog.rawDate).toISOString(),
            }),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
            "articleSection": blog.category,
          })}
        </script>
      </Helmet>
    <div className="bdp-main-container">
      <div className="bdp-left-content">
        <div className="bdp-featured-card">
          <h2>{blog.title}</h2>
          <h4 className="bdp-subtitle">{blog.subtitle}</h4>

          <span className="bdp-tag">{blog.category}</span>
          <p className="bdp-meta">
            {blog.author}   | 📆 <span>{blog.date}</span>   | 🕑 <span>{blog.time}</span>
          </p>
          <div
            className="bdp-image-box1"
            style={{
              backgroundImage: `url(${blog.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <p
            className="bdp-summary"
            dangerouslySetInnerHTML={{ __html: blog.summary }}
          />
          <div className="blogsp-icons">
            <i className="fab fa-facebook-f"></i>
            <i className="fab fa-twitter"></i>
            <i className="fab fa-instagram"></i>
            <i className="fas fa-link"></i>
          </div>
        </div>

        <div className="bdp-recent">
          <h2>
            <span className="bdp-highlightf">See</span> Related Posts
          </h2>
          <div className="bdp-featured-grid-scroll">
            {relatedBlogs.map((item, i) => (
              <Link to={`/blog/${item.slug}`} className="bdp-featured-card" key={i}>
                <span className="bdp-tag">{item.category}</span>
                <h3>{item.title}</h3>
                <div
                  className="bdp-image-box"
                  style={{
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <p className="bdp-meta">
                  {item.author}   | 📆 <span>{item.date}</span>   | 🕑 <span>{item.time}</span>
                </p>
                <p
                  className="bdp-summary"
                  dangerouslySetInnerHTML={{ __html: item.summary }}
                />
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="bdp-right-sidebar">
        <div className="bdp-authors-box">
          <h2>
            <span className="bdp-highlightf">Top</span> Authors
          </h2>
          {topAuthors.map((author, index) => (
            <div className="bdp-author-card" key={index}>
              <div className="bdp-author-avatar" />
              <div className="bdp-author-info">
                <h5>{author}</h5>
                
                <div className="bdp-social-icons">
                  <i className="fab fa-facebook-f"></i>
                  <i className="fab fa-twitter"></i>
                  <i className="fab fa-instagram"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

<div className="bdp-ad-box">
  <h4>
    Perform Better.
    <br />
    Recover Stronger.
  </h4>
  <p>
    Enhance athletic performance with expert sports science,
    physiotherapy, injury prevention, and personalized
    recovery strategies.
  </p>
  <Link to="/fitness" className="bdp-visit-btn">
    Explore Our Services
  </Link>
</div>

        <div className="blogsp-category-box">
          <h2>
            <span className="bdp-highlightf">Categories</span>
          </h2>
          <ul>
            {categories.map((category, index) => (
              <li key={index}>
                {category.name} <span>{category.count.toString().padStart(2, "0")}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bdp-update-box">
          <h2>
            <span className="bdp-highlightf">Today’s</span> Update
          </h2>
          <div className="bdp-update-grid">
            <div className="bdp-update-card">
              <h3>14</h3>
              <p>New Posts</p>
            </div>
            <div className="bdp-update-card">
              <h3>480</h3>
              <p>Total Visitors</p>
            </div>
            <div className="bdp-update-card">
              <h3>29</h3>
              <p>New Subscribers</p>
            </div>
            <div className="bdp-update-card">
              <h3>138</h3>
              <p>Blog Read</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
